import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, RotateCcw, Search, ShieldOff, Trash2, Upload, Users } from 'lucide-react';
import { toast } from 'sonner';

export function WasabiClientsCard() {
  const [stats, setStats] = useState({ total: 0, by_enseigne: [] });
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupResult, setDedupResult] = useState(null);
  const [showList, setShowList] = useState(false);

  const load = async (q = '') => {
    try {
      const params = new URLSearchParams({ per_page: '50' });
      if (q) params.set('search', q);
      const { data } = await api.get(`/wasabi-clients?${params.toString()}`);
      setStats({ total: data.total, by_enseigne: data.by_enseigne });
      setClients(data.clients || []);
    } catch {}
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Format .xlsx attendu');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/wasabi-clients/upload?replace=true', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${data.inserted} clients importés`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleReloadFromDisk = async () => {
    setUploading(true);
    try {
      const { data } = await api.post('/wasabi-clients/reload-from-disk');
      toast.success(`${data.inserted} clients chargés depuis le fichier interne`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setUploading(false);
    }
  };

  const runDedup = async (dryRun = true) => {
    setDedupLoading(true);
    try {
      const { data } = await api.post(`/wasabi-clients/dedup-prospects?dry_run=${dryRun}`);
      setDedupResult(data);
      if (dryRun) {
        toast.info(`${data.matched_prospects_count} doublon(s) détecté(s)`);
      } else {
        toast.success(`${data.deleted} prospect(s) supprimé(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setDedupLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await api.delete(`/wasabi-clients/${id}`);
      toast.success('Supprimé');
      load(search);
    } catch { toast.error('Erreur'); }
  };

  return (
    <Card className="border-2 border-[#10B981]/20 shadow-sm bg-gradient-to-br from-[#ECFDF5] to-white" data-testid="wasabi-clients-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#10B981]/15 flex items-center justify-center flex-shrink-0">
              <ShieldOff className="w-5 h-5 text-[#10B981]" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Parc client WASABI — Exclusions</h3>
              <p className="text-xs text-[#71717A] mt-0.5">Liste des clients existants à NE PAS démarcher. Upload à chaque mise à jour du parc.</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-[#10B981]">{stats.total}</div>
            <div className="text-[10px] text-[#71717A] uppercase tracking-wide">Clients</div>
          </div>
        </div>

        {stats.by_enseigne.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {stats.by_enseigne.slice(0, 8).map(e => (
              <span key={e.enseigne} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-white border border-[#D1FAE5]">
                <span className="font-medium">{e.enseigne}</span>
                <span className="text-[#71717A]">{e.count}</span>
              </span>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="relative h-10 rounded-lg border-2 border-dashed border-[#10B981]/40 bg-white hover:bg-[#ECFDF5] cursor-pointer flex items-center justify-center gap-2 text-sm font-medium text-[#059669] transition-colors" data-testid="wasabi-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
            {uploading ? 'Import...' : 'Uploader fichier .xlsx'}
            <input type="file" accept=".xlsx" onChange={handleUpload} disabled={uploading} className="absolute inset-0 opacity-0 cursor-pointer"/>
          </label>
          <Button variant="outline" onClick={() => runDedup(true)} disabled={dedupLoading || stats.total === 0} data-testid="wasabi-dedup-dryrun-btn"
            className="h-10 border-[#F59E0B]/40 text-[#B45309] hover:bg-[#FFFBEB]">
            {dedupLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4 mr-1.5"/>}
            Vérifier doublons
          </Button>
          <Button variant="outline" onClick={() => { if (window.confirm("Supprimer TOUS les prospects en doublon avec le parc client ?")) runDedup(false); }}
            disabled={dedupLoading || stats.total === 0} data-testid="wasabi-dedup-delete-btn"
            className="h-10 border-[#EF4444]/40 text-[#DC2626] hover:bg-[#FEF2F2]">
            <Trash2 className="w-4 h-4 mr-1.5"/>Supprimer doublons
          </Button>
        </div>

        {stats.total === 0 && (
          <Button variant="outline" onClick={handleReloadFromDisk} disabled={uploading}
            className="w-full h-9 text-xs border-[#10B981]/40 text-[#059669] hover:bg-[#ECFDF5]" data-testid="wasabi-reload-disk-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <RotateCcw className="w-3.5 h-3.5 mr-1.5"/>}
            Initialiser depuis le fichier interne (Parc client Wasabi VF.xlsx)
          </Button>
        )}

        {dedupResult && (
          <div className="bg-[#FFFBEB] border border-[#FCD34D] rounded-lg p-3 text-xs space-y-1">
            <div className="font-semibold">
              {dedupResult.matched_prospects_count} prospect(s) correspondent au parc client
              {!dedupResult.dry_run && <> • <span className="text-[#DC2626]">{dedupResult.deleted} supprimé(s)</span></>}
            </div>
            {dedupResult.sample.length > 0 && dedupResult.dry_run && (
              <ul className="space-y-0.5 mt-1 max-h-40 overflow-auto">
                {dedupResult.sample.map((m, i) => (
                  <li key={i} className="text-[11px]">
                    • <strong>{m.prospect.enseigne}</strong> {m.prospect.ville} ↔ client {m.matched_client.enseigne} {m.matched_client.ville}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button onClick={() => setShowList(v => !v)} className="text-xs text-[#059669] hover:underline font-medium" data-testid="wasabi-toggle-list-btn">
          {showList ? 'Masquer la liste' : `Voir la liste (${stats.total})`}
        </button>

        {showList && (
          <div className="space-y-2 border-t border-[#E5E7EB] pt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#71717A]"/>
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher enseigne, ville, CP..."
                className="pl-9 h-9 bg-white" data-testid="wasabi-search-input"/>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1">
              {clients.length === 0 && <div className="text-xs text-[#71717A] text-center py-4">Aucun client</div>}
              {clients.map(c => (
                <div key={c.id} className="flex items-center gap-2 p-2 bg-white rounded border border-[#E5E7EB] text-xs">
                  <Users className="w-3 h-3 text-[#10B981] flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{c.enseigne} — {c.ville}</div>
                    <div className="text-[#71717A] truncate">{c.rue} {c.code_postal}</div>
                  </div>
                  <button onClick={() => handleDelete(c.id)} className="text-[#71717A] hover:text-[#EF4444]" data-testid={`wasabi-delete-${c.id}`}>
                    <Trash2 className="w-3 h-3"/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
